import createClient, { type Middleware } from 'openapi-fetch';
import {
  API_AUDIENCE,
  isInteractionRequired,
  isJwtShaped,
  tokenAudienceMatches,
} from '../auth/auth0Client';
import { getAccessToken } from '../auth/tokenSource';
import { RyptError, newRequestId } from './errors';
import type { paths } from './schema';

/**
 * '' in dev, so every /v1 path stays relative and the Vite proxy makes the hop
 * server-side — the browser never goes cross-origin and CORS never applies.
 * 'https://api.rypt.dev' in production, which is the one origin the API allows.
 */
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export const api = createClient<paths>({
  baseUrl: API_BASE,
  // The API deliberately does not set Access-Control-Allow-Credentials. 'include' would
  // make the browser refuse every response, so this is not a default worth inheriting.
  credentials: 'omit',
  // Late-bound on purpose. openapi-fetch defaults to `fetch: globalThis.fetch`, captured
  // when createClient runs -- which is module-eval time. Anything that replaces
  // globalThis.fetch afterwards (MSW in tests, an instrumentation shim in production)
  // would then be invisible to this client, and requests would quietly bypass it.
  fetch: (input: Request) => globalThis.fetch(input),
});

const bearer: Middleware = {
  async onRequest({ request }) {
    const ctx = {
      op: `${request.method} ${new URL(request.url, window.location.origin).pathname}`,
      // call() already put an id on the wire; never mint a second one for the same call.
      requestId: request.headers.get('x-request-id') ?? newRequestId(),
    };

    let token: string | undefined;
    try {
      token = await getAccessToken();
    } catch (cause) {
      if (isInteractionRequired(cause)) throw RyptError.interactionRequired(cause, ctx);
      throw RyptError.transport(cause, ctx);
    }

    if (!token) throw RyptError.interactionRequired(new Error('no access token'), ctx);
    if (!isJwtShaped(token)) {
      throw RyptError.config('opaque_token', 'Auth0 returned an opaque access token', ctx);
    }
    if (!tokenAudienceMatches(token)) {
      throw RyptError.config('wrong_audience', `the access token is not for ${API_AUDIENCE}`, ctx);
    }

    request.headers.set('authorization', `Bearer ${token}`);
    return request;
  },
};

api.use(bearer);

/* ---- the last request id, for the support section of Settings ----
 *
 * rypt reuses a well-formed client `x-request-id` verbatim and writes it into
 * `audit_events.request_id`, so the id of the most recent call is the one thing a user
 * can quote that points an operator straight at their row. It is kept here, next to the
 * client that mints it, rather than threaded through every hook that might want it.
 *
 * Shaped for useSyncExternalStore: subscribe returns its own unsubscribe, and the
 * snapshot is a plain string that only changes when a new call goes out.
 */
type RequestIdListener = () => void;
const requestIdListeners = new Set<RequestIdListener>();
let lastRequestId: string | null = null;

export function noteRequestId(id: string): void {
  if (id === lastRequestId) return;
  lastRequestId = id;
  for (const fn of requestIdListeners) fn();
}

export function onRequestId(fn: RequestIdListener): () => void {
  requestIdListeners.add(fn);
  return () => {
    requestIdListeners.delete(fn);
  };
}

export const getLastRequestId = (): string | null => lastRequestId;
